import React from 'react';
import { ApolloError } from 'apollo-client';
import moment from 'moment-timezone';
import { CoachingUserType } from 'globalTypes';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import { CoachingSessionContextProvider } from 'coaching/components/CoachingCoordinatorView/SharedCoordinatorSessionRowComponents';
import CoachingSessionRow from 'coaching/components/CoachingCoordinatorView/CoachingSessionRow';
import Divider from 'components/Divider';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Link, { Props as LinkProps } from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import ToastActions from 'actions/ToastActions';
import LinkConstants from 'constants/LinkConstants';
import CoachingConstants from 'coaching/CoachingConstants';
import { deleteStudentInvitation } from 'coaching/actions/CoachingActions';
import TimezoneHelper from 'lib/timezones';
import {
  CoachingSessionGQL,
  DistanceLearningSessionGQL,
} from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import { ParticipantRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';

export type Props = {
  participant: ParticipantRow;
  onShowScheduleSessionView?: () => void;
  upcomingSessions?: CoachingSessionGQL[];
  previousSessions?: CoachingSessionGQL[];
  sessionMedia?: DistanceLearningSessionGQL[];
  sessionsLoading: boolean;
  sessionsError?: ApolloError;
};

type State = {
  showUpcomingSessions: boolean;
  showPreviousSessions: boolean;
  showSessionMedia: boolean;
};

function formatRecordingDate(unixTimestampInMilliseconds: string): string {
  const timestampInSeconds = Number(unixTimestampInMilliseconds) / 1000;
  return moment.unix(timestampInSeconds).format('MMM DD, YYYY');
}

export default class CoachingCoordinatorParticipantSidebar extends React.Component<Props, State> {
  state = {
    showPreviousSessions: false,
    showUpcomingSessions: false,
    showSessionMedia: false,
  };

  trackEvent(event: string) {
    EventTracking.trackKey(
      `${CoachingConstants.COORDINATOR_TOOL_EVENT_PREFIX}.participant_sidebar.${event}`
    );
  }

  togglePreviousSessionsVisibility = () => {
    this.setState({ showPreviousSessions: !this.state.showPreviousSessions }, () => {
      if (this.state.showPreviousSessions) {
        this.trackEvent('past_sessions.view');
      }
    });
  };

  toggleUpcomingSessionsVisibility = () => {
    this.setState({ showUpcomingSessions: !this.state.showUpcomingSessions }, () => {
      if (this.state.showUpcomingSessions) {
        this.trackEvent('upcoming_sessions.view');
      }
    });
  };

  toggleSessionMediaVisibility = () => {
    this.setState({ showSessionMedia: !this.state.showSessionMedia }, () => {
      if (this.state.showSessionMedia) {
        this.trackEvent('recordings.view');
      }
    });
  };

  renderSessions = (
    showSessions: boolean,
    loading: boolean,
    isPast: boolean,
    sessions?: CoachingSessionGQL[],
    error?: ApolloError
  ) => {
    if (!showSessions) {
      return null;
    }

    const loadingState = this.renderLoading('sessions', loading, error, sessions);
    if (loadingState) {
      return loadingState;
    }

    const { participant } = this.props;

    return sessions!.map(session => (
      <CoachingSessionContextProvider key={session.uuid}>
        <CoachingSessionRow
          participantUuid={participant.uuid}
          userName={participant.studentName}
          userType={CoachingUserType.participant}
          session={session}
          isPastEvent={isPast}
          key={session.uuid}
        />
      </CoachingSessionContextProvider>
    ));
  };

  renderMedia = (showMedia: boolean, sessionMedia: DistanceLearningSessionGQL[]) => {
    if (!showMedia) {
      return null;
    }

    return (
      <BoxBody size="xxsmall">
        {sessionMedia.map((media: DistanceLearningSessionGQL) => (
          <Row size="xsmall" data-test="coaching-session-media">
            <Link target="_blank" href={media.s3ResourceUri}>
              <Text size="medium" data-test="coaching-session-media-recording-date">
                {formatRecordingDate(media.createdAt)}
              </Text>
            </Link>
          </Row>
        ))}
      </BoxBody>
    );
  };

  renderLoading = (
    dataType: 'sessions' | 'media',
    loading: boolean,
    error?: ApolloError,
    data?: any[]
  ) => {
    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (error) {
      return (
        <ErrorReporter
          error={`CoachingCoordinatorParticipantSidebar - couldn't load upcoming ${dataType} error=${error}`}>
          <Text color="graphite">Could not load {dataType}</Text>
        </ErrorReporter>
      );
    }

    return null;
  };

  renderHeader = (headerText: string, showSessions: boolean, sessionsLength?: number) => {
    const iconName = showSessions ? 'chevron-down' : 'chevron-right';

    return (
      <Text color="chalkboard" element="div" lineHeight="flush">
        <MediaObject center figure="right">
          <Heading
            level="4"
            flush={!showSessions && sessionsLength !== undefined && sessionsLength > 0}>
            {headerText}{' '}
            {!this.props.sessionsLoading && sessionsLength !== undefined && `(${sessionsLength})`}
          </Heading>
          {sessionsLength === 0 ? <span /> : <Icon name={iconName} size="small" />}
        </MediaObject>
      </Text>
    );
  };

  renderEmptyList = (text: string) => {
    return (
      <Text color="graphite" size="small">
        {text}
      </Text>
    );
  };

  renderSessionSection = (
    sectionName: 'Past' | 'Upcoming',
    showSessions: boolean,
    toggleSessionVisibility: () => void,
    sessions?: CoachingSessionGQL[]
  ) => {
    const { sessionsLoading, sessionsError } = this.props;
    const noSessions = sessions?.length === 0;
    const isPast = sectionName === 'Past';
    return (
      <React.Fragment>
        <Link
          disabled={noSessions}
          onClick={noSessions ? undefined : toggleSessionVisibility}
          data-test={`toggle-sessions-${sectionName}`}>
          {this.renderHeader(sectionName, showSessions, sessions && sessions.length)}
        </Link>
        {noSessions
          ? this.renderEmptyList('No sessions yet')
          : this.renderSessions(showSessions, sessionsLoading, isPast, sessions, sessionsError)}
      </React.Fragment>
    );
  };

  renderSessionMediaSection = (
    showMedia: boolean,
    toggleVisibility: () => void,
    sessionMedia?: DistanceLearningSessionGQL[]
  ) => {
    const { sessionsLoading, sessionsError } = this.props;
    const media = sessionMedia?.slice(0, CoachingConstants.COACHING_SESSION_RECORDINGS_LIMIT) ?? [];
    const noMedia = media.length === 0;

    const loadingState = this.renderLoading('media', sessionsLoading, sessionsError, media);
    if (loadingState) {
      return loadingState;
    }

    return (
      <React.Fragment>
        <Link
          disabled={noMedia}
          onClick={noMedia ? undefined : toggleVisibility}
          data-test="toggle-sessions-media">
          {this.renderHeader('Recordings', showMedia, media.length)}
        </Link>
        {noMedia ? this.renderEmptyList('No recordings yet') : this.renderMedia(showMedia, media!)}
      </React.Fragment>
    );
  };

  render() {
    const { participant, onShowScheduleSessionView } = this.props;
    function copyToClipboard(text: string, label: string) {
      navigator.clipboard.writeText(text);
      ToastActions.showSuccessToast(`Copied ${label} to clipboard`);
    }
    const linkProps: LinkProps = {
      target: '_blank',
      weight: 'semibold',
    };
    const adminLinks = [
      <Link
        href={`${LinkConstants.R101_ADMIN_USER_BY_UUID_URL}${participant.purchaserUuid}`}
        {...linkProps}>
        User who purchased
      </Link>,
    ];
    const uuidLinks = [
      <Link onClick={() => copyToClipboard(participant.uuid, 'Participant UUID')} {...linkProps}>
        Participant UUID
      </Link>,
      <Link
        onClick={() => copyToClipboard(participant.purchaserUuid, 'Purchaser UUID')}
        {...linkProps}>
        Purchaser UUID
      </Link>,
    ];

    if (participant.studentUuid) {
      adminLinks.push(
        <Link
          href={`${LinkConstants.R101_ADMIN_USER_BY_UUID_URL}${participant.studentUuid}`}
          {...linkProps}>
          Student user
        </Link>
      );
      uuidLinks.push(
        <Link
          onClick={() => copyToClipboard(participant.studentUuid, 'Student UUID')}
          {...linkProps}>
          Student UUID
        </Link>
      );
    }

    return (
      <BoxBody size="small">
        <Heading level="2" serif>
          {participant.studentName}
        </Heading>
        <Divider size="medium" />
        <Row size="xxsmall">
          <Text color="graphite" size="xxxsmall" weight="semibold">
            UUIDs (click to copy)
          </Text>
        </Row>
        <Row size="small">
          {uuidLinks.reduce((prev, curr) => (
            <>
              {prev} • {curr}
            </>
          ))}
        </Row>
        <Row size="xxsmall">
          <Text color="graphite" size="xxxsmall" weight="semibold">
            Links to r101-admin
          </Text>
        </Row>
        {adminLinks.reduce((prev, curr) => (
          <>
            {prev} • {curr}
          </>
        ))}
        <Divider size="medium" />
        {participant.subjects.length > 0 ? (
          <Row size="small">
            <TextWithIcon icon="book">{participant.subjects.join(', ')}</TextWithIcon>
          </Row>
        ) : null}
        {participant.studentGradeLevel ? (
          <Row size="small">
            <TextWithIcon icon="apple">Grade {participant.studentGradeLevel}</TextWithIcon>
          </Row>
        ) : null}
        <Row size="small">
          <TextWithIcon icon="globe">
            {TimezoneHelper.convertToReadableShortCode(participant.timezone)}
          </TextWithIcon>
        </Row>
        <Row>
          <TextWithIcon icon="link">
            <Link
              onClick={() => copyToClipboard(participant.whiteboardUrl!, 'whiteboard link')}
              {...linkProps}>
              Whiteboard link
            </Link>{' '}
            (click to copy)
          </TextWithIcon>
        </Row>
        <Buttons full group>
          {onShowScheduleSessionView && (
            <Button
              level="secondary"
              data-test="add-session-button"
              onClick={() => {
                this.trackEvent('add_session_button.click');
                onShowScheduleSessionView();
              }}>
              <Text size="xxxsmall">Add session</Text>
            </Button>
          )}
          <Button
            full={!onShowScheduleSessionView}
            level="secondary"
            data-test="session-video-button"
            target="_blank"
            to={`${CoachingConstants.COACHING_SESSION_ROUTE}/${participant.distanceLearningPermalink}`}>
            <Icon name="video-camera" size="medium" />
          </Button>
        </Buttons>
        <Divider size="medium" />
        {this.renderSessionSection(
          'Upcoming',
          this.state.showUpcomingSessions,
          this.toggleUpcomingSessionsVisibility,
          this.props.upcomingSessions
        )}
        <Divider size="medium" />
        {this.renderSessionSection(
          'Past',
          this.state.showPreviousSessions,
          this.togglePreviousSessionsVisibility,
          this.props.previousSessions
        )}
        <Divider size="medium" />
        {this.renderSessionMediaSection(
          this.state.showSessionMedia,
          this.toggleSessionMediaVisibility,
          this.props.sessionMedia
        )}
        <Divider size="medium" />
        <Link
          data-test="delete-student-invitation-link"
          color="apple"
          weight="semibold"
          onClick={() => deleteStudentInvitation(participant.uuid)}>
          Delete student invitation
        </Link>
      </BoxBody>
    );
  }
}
